import React, { Suspense } from 'react'
import classNames from 'classnames'
import dayjs from 'dayjs'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import rehypeRaw from 'rehype-raw'
import rehypeSanitize from 'rehype-sanitize'
import { HiUser } from 'react-icons/hi'
import { HiSparkles } from 'react-icons/hi2'
import useThemePalette from 'utils/hooks/useThemePalette'
import ChatArtifacts from './ChatArtifacts'

const SyntaxHighlighter = React.lazy(() => import('./SyntaxHighlighter'))

const normalizeCodeLanguage = (language = '') => {
    const normalized = language.toLowerCase()
    if (['ps1', 'pwsh', 'powershell'].includes(normalized)) return 'powershell'
    if (normalized === 'shell') return 'bash'
    return normalized
}

const MessageAttachments = ({ attachments = [], isDark = false, isUser = false }) => {
    const normalizedAttachments = Array.isArray(attachments)
        ? attachments.filter((attachment) => attachment?.previewUrl)
        : []

    if (normalizedAttachments.length === 0) {
        return null
    }

    return (
        <div className="mb-3 flex flex-wrap gap-3">
            {normalizedAttachments.map((attachment) => (
                <a
                    key={attachment.id || attachment.previewUrl}
                    href={attachment.previewUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={classNames(
                        'group block w-36 overflow-hidden rounded-xl border shadow-sm transition hover:shadow-md',
                        isUser
                            ? 'border-white/20 bg-white/10'
                            : isDark
                                ? 'border-gray-600 bg-gray-800'
                                : 'border-gray-200 bg-white'
                    )}
                >
                    <img
                        src={attachment.previewUrl}
                        alt={attachment.fileName || 'Chat attachment'}
                        className="h-28 w-full object-cover transition duration-200 group-hover:scale-[1.02]"
                    />
                    <div
                        className={classNames(
                            'border-t px-2.5 py-2 text-[11px] leading-4',
                            isUser
                                ? 'border-white/10 text-blue-50'
                                : isDark
                                    ? 'border-gray-700 text-gray-300'
                                    : 'border-gray-100 text-gray-600'
                        )}
                    >
                        <p className="truncate font-medium" title={attachment.fileName || 'Attached image'}>
                            {attachment.fileName || 'Attached image'}
                        </p>
                    </div>
                </a>
            ))}
        </div>
    )
}

const ChatMessage = ({
    message,
    isUser = false,
    timestamp,
    attachments = [],
    artifacts = [],
}) => {
    const palette = useThemePalette()
    const { isDark } = palette
    const content = typeof message === 'string' ? message : String(message ?? '')

    // Dark-mode-aware bubble colors
    const assistantBubble = isDark
        ? 'bg-gray-700 text-gray-100 border border-gray-600'
        : 'bg-gray-100 text-gray-900 border border-gray-200'

    const userBubble = isDark
        ? 'bg-blue-600 text-white'
        : 'bg-blue-500 text-white'

    const assistantCode = isDark
        ? 'bg-gray-900/60 text-gray-200'
        : 'bg-black/10 text-gray-800'

    const assistantPre = isDark
        ? 'bg-gray-900 border border-gray-600 text-gray-100'
        : 'bg-black/10 text-gray-800'

    const assistantLink = isDark ? 'text-blue-300 underline' : 'text-blue-600 underline'

    return (
        <div
            className={classNames(
                'flex items-end gap-2 mb-4',
                isUser ? 'flex-row-reverse' : 'flex-row'
            )}
        >
            {/* Avatar */}
            <div
                className={classNames(
                    'flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-white text-xs mb-1',
                    isUser
                        ? 'bg-blue-500'
                        : isDark ? 'bg-gray-600' : 'bg-gray-400'
                )}
            >
                {isUser ? <HiUser className="w-4 h-4" /> : <HiSparkles className="w-4 h-4" />}
            </div>

            {/* Bubble */}
            <div
                className={classNames(
                    'max-w-[85%] lg:max-w-[80%] px-4 py-3 rounded-2xl shadow-sm',
                    isUser
                        ? `${userBubble} rounded-br-sm`
                        : `${assistantBubble} rounded-bl-sm`
                )}
            >
                <MessageAttachments
                    attachments={attachments}
                    isDark={isDark}
                    isUser={isUser}
                />
                {isUser ? (
                    content ? (
                        <p className="text-sm whitespace-pre-wrap break-words leading-relaxed">{content}</p>
                    ) : null
                ) : (
                    <ReactMarkdown
                        className="text-sm break-words max-w-none leading-relaxed"
                        remarkPlugins={[remarkGfm]}
                        rehypePlugins={[rehypeRaw, rehypeSanitize]}
                        components={{
                            a: ({ node, children, ...props }) => (
                                <a {...props} target="_blank" rel="noopener noreferrer" className={assistantLink}>
                                    {children}
                                </a>
                            ),
                            p: ({ node, children, ...props }) => <p {...props} className="mb-2 last:mb-0">{children}</p>,
                            ul: ({ node, children, ...props }) => <ul {...props} className="list-disc pl-5 mb-2 space-y-0.5">{children}</ul>,
                            ol: ({ node, children, ...props }) => <ol {...props} className="list-decimal pl-5 mb-2 space-y-0.5">{children}</ol>,
                            li: ({ node, children, ...props }) => <li {...props} className="leading-relaxed">{children}</li>,
                            strong: ({ node, children, ...props }) => (
                                <strong {...props} className={isDark ? 'font-semibold text-white' : 'font-semibold text-gray-900'}>{children}</strong>
                            ),
                            h1: ({ node, children, ...props }) => <h1 {...props} className="text-base font-bold mb-2 mt-1">{children}</h1>,
                            h2: ({ node, children, ...props }) => <h2 {...props} className="text-sm font-bold mb-1.5 mt-1">{children}</h2>,
                            h3: ({ node, children, ...props }) => <h3 {...props} className="text-sm font-semibold mb-1 mt-1">{children}</h3>,
                            blockquote: ({ node, children, ...props }) => (
                                <blockquote
                                    {...props}
                                    className={classNames(
                                        'border-l-4 pl-3 my-2 italic',
                                        isDark ? 'border-gray-500 text-gray-300' : 'border-gray-300 text-gray-600'
                                    )}
                                >
                                    {children}
                                </blockquote>
                            ),
                            code: ({ inline, className, children, ...props }) => {
                                const match = /language-(\w+)/.exec(className || '')
                                const language = normalizeCodeLanguage(match?.[1] || '')
                                const code = String(children).replace(/\n$/, '')

                                return inline || !language ? (
                                    <code
                                        {...props}
                                        className={classNames(
                                            'px-1.5 py-0.5 rounded text-xs font-mono',
                                            assistantCode
                                        )}
                                    >
                                        {children}
                                    </code>
                                ) : (
                                    <Suspense
                                        fallback={
                                            <pre className={classNames('rounded-lg p-3 overflow-x-auto mb-2 text-xs font-mono', assistantPre)}>
                                                <code>{code}</code>
                                            </pre>
                                        }
                                    >
                                        <SyntaxHighlighter
                                            language={language}
                                            PreTag="div"
                                            customStyle={{
                                                borderRadius: '0.5rem',
                                                fontSize: '0.75rem',
                                                margin: '0 0 0.5rem',
                                                padding: '0.875rem',
                                            }}
                                            codeTagProps={{ className: 'font-mono' }}
                                        >
                                            {code}
                                        </SyntaxHighlighter>
                                    </Suspense>
                                )
                            },
                            pre: ({ node, children }) => <>{children}</>,
                            hr: ({ node, ...props }) => (
                                <hr {...props} className={isDark ? 'border-gray-600 my-2' : 'border-gray-200 my-2'} />
                            ),
                        }}
                    >
                        {content}
                    </ReactMarkdown>
                )}
                {!isUser && <ChatArtifacts artifacts={artifacts} isDark={isDark} />}
                {timestamp && (
                    <p
                        className={classNames(
                            'text-xs mt-1.5 select-none',
                            isUser ? 'text-blue-200' : isDark ? 'text-gray-400' : 'text-gray-400'
                        )}
                    >
                        {dayjs(timestamp).format('h:mm A')}
                    </p>
                )}
            </div>
        </div>
    )
}

export default React.memo(ChatMessage)